import { useState } from 'react';
import { FinancePageLayout } from '@/components/finance';
import { useCommissionPlans, useDeleteCommissionPlan } from '@/hooks/finance/useCommissionPlans';
import type { CommissionPlanWithDetails } from '@/hooks/finance/useCommissionPlans';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import CommissionPlanForm from '@/components/finance/CommissionPlanForm';

export default function CommissionRules() {
  const { data: plans, isLoading } = useCommissionPlans('us');
  const deletePlan = useDeleteCommissionPlan();
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const [editingPlan, setEditingPlan] = useState<CommissionPlanWithDetails | null>(null);
  const [showForm, setShowForm] = useState(false);

  const handleClose = () => {
    setShowForm(false);
    setEditingPlan(null);
  };

  if (showForm || editingPlan) {
    return (
      <FinancePageLayout>
        <CommissionPlanForm
          plan={editingPlan}
          onClose={handleClose}
        />
      </FinancePageLayout>
    );
  }

  return (
    <FinancePageLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Regras de Comissão</h1>
          <p className="text-muted-foreground">Gerencie planos de comissão por período e BU</p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Plano
        </Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Carregando...</div>
      ) : !plans?.length ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Nenhum plano de comissão cadastrado.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {plans.map((plan) => {
            const isExpanded = expandedPlan === plan.id;
            return (
              <Card key={plan.id}>
                <CardHeader className="cursor-pointer" onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-lg">{plan.name}</CardTitle>
                      <Badge variant={plan.status === 'active' ? 'default' : 'secondary'}>
                        {plan.status === 'active' ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {plan.effective_from}{plan.effective_to ? ` → ${plan.effective_to}` : ' →'}
                      </span>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setEditingPlan(plan); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remover plano?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Isso removerá o plano "{plan.name}" e todos os tiers/aceleradores associados.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deletePlan.mutate(plan.id)}>Remover</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </div>
                  <CardDescription>
                    Base: {plan.commission_base} | Ramp-up: {plan.rampup_type || 'Nenhum'} ({plan.rampup_duration_days}d) | Min. Entrada: {plan.min_entry_pct}%
                  </CardDescription>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-2">Escada de Comissão</h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>% Meta Atingida</TableHead>
                            <TableHead>Comissão</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {plan.commission_tiers.map((tier) => (
                            <TableRow key={tier.id}>
                              <TableCell>
                                {tier.min_goal_pct}% – {tier.max_goal_pct ? `${tier.max_goal_pct}%` : '∞'}
                              </TableCell>
                              <TableCell className="font-semibold">{tier.commission_pct}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {plan.commission_accelerators.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">Aceleradores (máx. +{plan.max_accelerator_pct}%)</h3>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nome</TableHead>
                              <TableHead>Bônus</TableHead>
                              <TableHead>Descrição</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {plan.commission_accelerators.map((acc) => (
                              <TableRow key={acc.id}>
                                <TableCell className="font-medium">{acc.name}</TableCell>
                                <TableCell>+{acc.bonus_pct}%</TableCell>
                                <TableCell className="text-muted-foreground">{acc.description}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {plan.rampup_type && plan.rampup_config && Object.keys(plan.rampup_config).length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">Ramp-up Config</h3>
                        <div className="flex gap-4">
                          {Object.entries(plan.rampup_config).map(([key, val]) => (
                            <Badge key={key} variant="outline">
                              {key.replace('_', ' ')}: +{val}%
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </FinancePageLayout>
  );
}
